import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServiceRouter } from '@pershing/pershing-core';
import { MutualFundTradingServiceDefinition } from './../../models/mutualfund/mutualfund-trading-service-definition';
import { TransactionType, AccountType, Account, MFDvreType } from '../../models/common/trading-common.model';
import {
    MutualfundTradingOrder,
    RedemptionFeeWaiver,
    ShortSettle,
    CdscFee,
    CdscFeeRequestWrapper,
    DistributionType,
    PayToType,
    GrossNetIndicator,
    CDSCWaiver
} from '../../models/mutualfund/trading-mutualfund.model';

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    })
};

@Injectable()
export abstract class MutualFundTradingServiceStrategy {

    private baseUrl: string;
    private platform: string;
    acctNumParam = 'urn:id:headers.x-AccountNumber';
    ordDltParam = 'urn:id:headers.x-OrdDtlKey';

    constructor (protected http: HttpClient, protected serviceRouter: ServiceRouter, @Optional() @Inject('AppSettings') appSettings: any) {
        if (appSettings?.platform) {
            this.platform = appSettings.platform;
        } else {
            this.platform = 'professional';
        }
        this.baseUrl = this.serviceRouter.getServiceRouteURL(MutualFundTradingServiceDefinition.getInstance(this.platform), { ignoreOptions: { serviceVersion: true } });
    }

    getHttpHeadersWithAcctNum(acctNum: string) {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-AccountNumber': acctNum
        });
    }

    private getHeadersWithAcctNumAndOrdDltKey(acctNum: string,ordDtlKey: string) {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-AccountNumber': acctNum,
            'x-OrdDtlKey': ordDtlKey
        });
    }

    public abstract support(platform: string): boolean;

    public abstract editMutualFundOrder(mutualFundTradingOrder: MutualfundTradingOrder): Observable<any>;

    public getBaseUrl(): string {
        return this.baseUrl;
    }

    public getTransactionTypes(account: Account): TransactionType[] {
        // Confirmed that transaction types are the same for retail and pro,
        // with no special handling based on account properties like with Equities
        let data: TransactionType[] = [];
        data.push(TransactionType.BUY);

        // Exchange transactions aren't allowed in a Level 4 Plan 529 account
        if ( !account || !account.beingTradedInLevel4PlanAcct ) {
            data.push(TransactionType.REDEEM);
            data.push(TransactionType.REDEEM_ALL);
            data.push(TransactionType.EXCHANGE);
            data.push(TransactionType.EXCHANGE_ALL);
        }

        return data;
    }

    public getMFDvreTypes(divindInd: string, capitalGainInd: string): MFDvreType[] {
        let mfDvreTypes: MFDvreType[] = [];
        if (divindInd === 'Y' && capitalGainInd === 'N') {
          mfDvreTypes.push(MFDvreType.DIVIDEND);
          mfDvreTypes.push(MFDvreType.CASH);
        } else if (divindInd === 'N' && capitalGainInd === 'Y') {
          mfDvreTypes.push(MFDvreType.CAPITAL);
          mfDvreTypes.push(MFDvreType.CASH);
        } else if (divindInd === 'N' && capitalGainInd === 'N') {
          mfDvreTypes.push(MFDvreType.CASH);
        } else {
          mfDvreTypes.push(MFDvreType.REINVEST);
          mfDvreTypes.push(MFDvreType.DIVIDEND);
          mfDvreTypes.push(MFDvreType.CAPITAL);
          mfDvreTypes.push(MFDvreType.CASH);
        }

        return mfDvreTypes;
    }

    public getCdscWaiverTypes(): CDSCWaiver[] {
        let cdscWaiverTypes: CDSCWaiver[] = [];
        cdscWaiverTypes.push(CDSCWaiver.NO);
        cdscWaiverTypes.push(CDSCWaiver.DUE_TO_CORRECTION);
        cdscWaiverTypes.push(CDSCWaiver.DUE_TO_DEATH);
        cdscWaiverTypes.push(CDSCWaiver.DUE_TO_DISABILITY);
        cdscWaiverTypes.push(CDSCWaiver.DUE_TO_MANDATORY_DISTRIBUTION);
        cdscWaiverTypes.push(CDSCWaiver.DUE_TO_DEFINED_CONTRIBUTION);
        cdscWaiverTypes.push(CDSCWaiver.DUE_TO_HARDSHIP);
        cdscWaiverTypes.push(CDSCWaiver.DUE_TO_NO_COMMISSION_PAID);

        return cdscWaiverTypes;
    }

    public getGrossNetTypes(isFrontEndLoadFund: boolean): GrossNetIndicator[] {
        let grossNetTypes: GrossNetIndicator[] = [];
        grossNetTypes.push(GrossNetIndicator.GROSS);

        if ( !isFrontEndLoadFund ) {
            grossNetTypes.push(GrossNetIndicator.NET);
        }

        return grossNetTypes;
    }

    public getRedemptionFeeWaiverTypes(): RedemptionFeeWaiver[] {
        let redemptionFeeWaiverTypes: RedemptionFeeWaiver[] = [];
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.NO);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_CORRECTION);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_DEATH);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_DISABILITY);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_MANDATORY_DISTRIBUTION);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_DEFINED_CONTRIBUTION);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_HARDSHIP);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_AUTO_REBALANCE);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_DC_TERMINATION_DISTRIBUTION);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_DC_INVOLUNTARY_DISTRIBUTION);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_DC_FORFEITURES);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_DC_LOANS);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_DC_EXCESSIVE_CONTRIBUTIONS);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_WRAP_PROGRAM);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_FUND_OF_FUNDS);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_DIVORCE_QDRO);
        redemptionFeeWaiverTypes.push(RedemptionFeeWaiver.DUE_TO_PLAN_FEES_QDRO);

        return redemptionFeeWaiverTypes;
    }

    public getShortSettleTypes(): ShortSettle[] {
        let shortSettleTypes: ShortSettle[] = [];
        shortSettleTypes.push(ShortSettle.Y);
        shortSettleTypes.push(ShortSettle.N);

        return shortSettleTypes;
    }

    public getDistributionTypes(): DistributionType[] {
        let distributionTypes: DistributionType[] = [];
        distributionTypes.push(DistributionType.QUALIFIED);
        distributionTypes.push(DistributionType.NON_QUALIFIED);
        distributionTypes.push(DistributionType.ROLLOVER);
        return distributionTypes;
    }

    public getPayToTypes(): PayToType[] {
        let payToTypes: PayToType[] = [];
        payToTypes.push(PayToType.ACCOUNT_HOLDER);
        payToTypes.push(PayToType.BENEFICIARY);
        payToTypes.push(PayToType.ANOTHER_529_PROGRAM);
        return payToTypes;
    }

    public abstract getAccountTypes(account: Account, transactionType: TransactionType): AccountType[];

    public getMutualFundDetails(account: Account, mutualFundID: string): Observable<any> {
        let url = this.getBaseUrl() + '/' + this.acctNumParam + '/' + mutualFundID + '/fund-detail';
        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(account.acctNum) });
    }

    public getPlan529Funds(acctNum: string): Observable<any> {
        let url = this.getBaseUrl() + '/' + this.acctNumParam + '/529-funds';
        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(acctNum) });
    }

    public getMutualFundExchangeToList(accountNum: string, cusip: string): Observable<any> {
        let url = this.getBaseUrl() + '/' + this.acctNumParam + '/' + cusip + '/exchange-list';
        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(accountNum) });
    }

    public reviewMutualFundOrder(mutualFundTradingOrder: MutualfundTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/review', mutualFundTradingOrder, httpOptions);
    }

    public submitMutualFundOrder(mutualFundTradingOrder: MutualfundTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/submit', mutualFundTradingOrder, httpOptions);
    }

  

    public sendProspectusRequest(req: any) {
      return this.http.post(this.getBaseUrl() + '/fulfillment', req, httpOptions);
    }

    public cancelMutualFundOrder(acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
        let encodedOrdDtlKey = encodeURIComponent(ordDtlKey);
        let encodedType = encodeURIComponent('Mutual Fund');
        let url = this.getBaseUrl() + `/${this.acctNumParam}/order/${subRequestTypeCode}/${encodedType}/cancel/${this.ordDltParam}`;
        return this.http.delete(url, { headers: this.getHeadersWithAcctNumAndOrdDltKey(acctNum, encodedOrdDtlKey) });
    }

    public getOrderDetails(acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
        let encodedOrdDtlKey = encodeURIComponent(ordDtlKey);
        let encodedType = encodeURIComponent('Mutual Fund');
        let url = this.getBaseUrl() + `/${this.acctNumParam}/order/${subRequestTypeCode}/${encodedType}/detail/${this.ordDltParam}`;
        return this.http.get(url, { headers: this.getHeadersWithAcctNumAndOrdDltKey(acctNum, encodedOrdDtlKey) });
    }

    public abstract getCdscFee(cdscFeeRequestWrapper: CdscFeeRequestWrapper): Observable<CdscFee>;

    public abstract getLoiRoaDetails(acctNum: string, symbol: string, cusip: string, type: string): Observable<any>;

    public getOrderCommission(mutualFundTradingOrder: MutualfundTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/commission', mutualFundTradingOrder, httpOptions);
    }
}
